import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AdminProtected from '../components/AdminProtected';
import { 
  Settings, 
  Save, 
  Loader2,
  BookOpen,
  Clock,
  Target,
  RefreshCw
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminSettingsPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    mock_exam_questions: 10,
    mock_exam_duration: 12,
    mock_exam_passing_score: 60,
    final_exam_questions: 50,
    final_exam_duration: 60,
    final_exam_passing_score: 60
  });

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/exam-settings`);
      if (response.ok) {
        const data = await response.json();
        setSettings({
          mock_exam_questions: data.mock_exam_questions || 10,
          mock_exam_duration: data.mock_exam_duration || 12,
          mock_exam_passing_score: data.mock_exam_passing_score || 60,
          final_exam_questions: data.final_exam_questions || 50,
          final_exam_duration: data.final_exam_duration || 60,
          final_exam_passing_score: data.final_exam_passing_score || 60
        });
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Failed to fetch exam settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/exam-settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settings)
      });

      if (!response.ok) {
        throw new Error('Failed to save settings');
      }

      toast.success('Exam settings saved successfully');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    const numValue = parseInt(value) || 0;
    setSettings(prev => ({
      ...prev,
      [field]: numValue
    }));
  };

  return (
    <AdminProtected title="Admin Settings">
      <div className="min-h-screen flex flex-col bg-gray-50">
        <Header />
        
        <main className="flex-1 pt-20 pb-12 px-4">
          <div className="max-w-4xl mx-auto">
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
                <Settings className="text-blue-600" />
                Admin Settings
              </h1>
              <p className="text-gray-600 mt-2">
                Configure exam parameters and system settings
              </p>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              </div>
            ) : (
              <div className="space-y-6">
                {/* Mock Exam Settings */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <BookOpen className="text-blue-600" />
                      Mock Exam Settings
                    </CardTitle>
                    <CardDescription>
                      Configure the number of questions, duration, and passing score for mock exams
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="mock_questions" className="flex items-center gap-2">
                          <BookOpen size={16} />
                          Number of Questions
                        </Label>
                        <Input
                          id="mock_questions"
                          type="number"
                          min="1"
                          max="100"
                          value={settings.mock_exam_questions}
                          onChange={(e) => handleInputChange('mock_exam_questions', e.target.value)}
                          data-testid="mock-exam-questions"
                        />
                        <p className="text-xs text-gray-500">Questions randomly selected from pool</p>
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="mock_duration" className="flex items-center gap-2">
                          <Clock size={16} />
                          Duration (minutes)
                        </Label>
                        <Input
                          id="mock_duration"
                          type="number"
                          min="1"
                          max="180"
                          value={settings.mock_exam_duration}
                          onChange={(e) => handleInputChange('mock_exam_duration', e.target.value)}
                          data-testid="mock-exam-duration"
                        />
                        <p className="text-xs text-gray-500">Time allowed to complete exam</p>
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="mock_passing" className="flex items-center gap-2">
                          <Target size={16} />
                          Passing Score (%)
                        </Label>
                        <Input
                          id="mock_passing"
                          type="number"
                          min="1"
                          max="100"
                          value={settings.mock_exam_passing_score}
                          onChange={(e) => handleInputChange('mock_exam_passing_score', e.target.value)}
                          data-testid="mock-exam-passing-score"
                        />
                        <p className="text-xs text-gray-500">Minimum score to pass</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Final Exam Settings */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Target className="text-green-600" />
                      Final Exam Settings
                    </CardTitle>
                    <CardDescription>
                      Configure the default settings for final exams (can be overridden per school)
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="final_questions" className="flex items-center gap-2">
                          <BookOpen size={16} />
                          Number of Questions
                        </Label>
                        <Input
                          id="final_questions"
                          type="number"
                          min="1"
                          max="200"
                          value={settings.final_exam_questions}
                          onChange={(e) => handleInputChange('final_exam_questions', e.target.value)}
                          data-testid="final-exam-questions"
                        />
                        <p className="text-xs text-gray-500">Default for AI-generated exams</p>
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="final_duration" className="flex items-center gap-2">
                          <Clock size={16} />
                          Duration (minutes)
                        </Label>
                        <Input
                          id="final_duration"
                          type="number"
                          min="1"
                          max="300"
                          value={settings.final_exam_duration}
                          onChange={(e) => handleInputChange('final_exam_duration', e.target.value)}
                          data-testid="final-exam-duration"
                        />
                        <p className="text-xs text-gray-500">Time allowed to complete exam</p>
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="final_passing" className="flex items-center gap-2">
                          <Target size={16} />
                          Passing Score (%)
                        </Label>
                        <Input
                          id="final_passing"
                          type="number"
                          min="1"
                          max="100"
                          value={settings.final_exam_passing_score}
                          onChange={(e) => handleInputChange('final_exam_passing_score', e.target.value)}
                          data-testid="final-exam-passing-score"
                        />
                        <p className="text-xs text-gray-500">Minimum score to pass</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Actions */}
                <div className="flex justify-end gap-4">
                  <Button
                    variant="outline"
                    onClick={fetchSettings}
                    disabled={loading || saving}
                  >
                    <RefreshCw size={16} className="mr-2" />
                    Reset
                  </Button>
                  <Button
                    onClick={handleSave}
                    disabled={saving}
                    className="bg-blue-600 hover:bg-blue-700"
                    data-testid="save-settings-btn"
                  >
                    {saving ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Save size={16} className="mr-2" />
                    )}
                    Save Settings
                  </Button>
                </div>

                {/* Info Card */}
                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="pt-6">
                    <h3 className="font-semibold text-blue-800 mb-2">How Exam Settings Work</h3>
                    <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
                      <li><strong>Mock Exams:</strong> Questions are randomly selected from the question pool based on the number configured</li>
                      <li><strong>Final Exams:</strong> Questions are generated using AI from uploaded training materials</li>
                      <li><strong>Duration:</strong> Students must complete the exam within the time limit</li>
                      <li><strong>Passing Score:</strong> Students must achieve this percentage to pass the exam</li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        </main>

        <Footer />
      </div>
    </AdminProtected>
  );
};

export default AdminSettingsPage;
